import { AnalyticsStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { useLocation } from "react-router-dom";
import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';

import { CompareToggle } from '@shared-ui/retail-compare-toggle';
import { Experiment, ExperimentControl, ExperimentVariant, useExperiment } from '@shared-ui/experiment-context';
import { LodgingQueryParamsProvider } from "@shared-ui/search-tools-lodging";
import { SortAndFiltersViewType } from '@shared-ui/retail-sort-and-filter-lodging';
import { TOGGLE_PLACEMENT_SRP } from '@shared-ui/retail-compare-providers';
import { useClickstreamAnalytics } from '@shared-ui/clickstream-analytics-context';
import { useLocalization } from '@shared-ui/localization-context';
import { ViewExtraLarge, ViewLarge, ViewMedium, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { SearchResultsStore } from 'stores/search-results-store';
import { useViewTrackerContext } from 'src/common/stores/contexts/viewTracker';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSpacing } from 'uitk-react-spacing';

import { MapPreview } from 'src/common/components/search/map/map-preview';
import { ResultsSubHeader } from './results-sub-header';
import { UniversalSortFilterWrapper } from './sort-filter/universal-sort-filter';

export interface ResultsHeaderProps {
  analytics?: AnalyticsStore;
  searchResults?: SearchResultsStore;
  withMapButton?: boolean;
  withFilterButton?: boolean;
}

export const ResultsHeader: React.FC<ResultsHeaderProps> = inject('searchResults')(
  observer(({ searchResults, withMapButton, withFilterButton }: ResultsHeaderProps) => {
    const l10n = useLocalization();

    if (!searchResults) {
      return null;
    }

    const searchResultsHeaderWrapperRef = React.useRef(null);
    const { isLoading, loyaltyMessagingModel, resultMessages } = searchResults;

    const ResultsSubHeaderProps = {
      resultMessages,
      isLoading,
      l10n,
      loyaltyMessagingModel,
    };
    const buttonsProps = {
      dialogTitle: l10n.formatText('hotels.searchResults.filtersDialog.title'),
      withMapButton,
      withFilterButton,
    };

    return (
      <ErrorBoundary label="hotel.search.tools">
        <UitkSpacing margin={{ blockstart: 'one' }}>
          <LodgingQueryParamsProvider urlSearchParams={new URLSearchParams(useLocation().search)}>
            <section data-stid="results-header" ref={searchResultsHeaderWrapperRef}>
              <Viewport>
                <ViewSmall>
                  <Experiment name="Introduce_Quick_Sort_and_Filters_at_top_of_SRP_mweb">
                    <ExperimentControl>
                      <ButtonsParent {...buttonsProps} />
                      <CompareToggle placement={TOGGLE_PLACEMENT_SRP} />
                      <UniversalSortFilterWrapper view={SortAndFiltersViewType.APPLIED_FILTERS} />
                      <ResultsSubHeader {...ResultsSubHeaderProps} isQuickAccessFiltersActive={false} />
                    </ExperimentControl>
                    <ExperimentVariant bucket={1}>
                      <CompareToggle placement={TOGGLE_PLACEMENT_SRP} />
                      <ResultsSubHeader {...ResultsSubHeaderProps} isQuickAccessFiltersActive={true} />
                    </ExperimentVariant>
                  </Experiment>
                </ViewSmall>
                <ViewMedium>
                  <Experiment name="Introduce_Quick_Sort_and_Filters_at_top_of_SRP_web">
                    <ExperimentControl>
                      {<ButtonsParent {...(buttonsProps as any)} />}
                      <CompareToggle placement={TOGGLE_PLACEMENT_SRP} />
                      <UniversalSortFilterWrapper view={SortAndFiltersViewType.APPLIED_FILTERS} />
                      <ResultsSubHeader {...ResultsSubHeaderProps} isQuickAccessFiltersActive={false} />
                    </ExperimentControl>
                    <ExperimentVariant bucket={1}>
                      <CompareToggle placement={TOGGLE_PLACEMENT_SRP} />
                      <ResultsSubHeader {...ResultsSubHeaderProps} isQuickAccessFiltersActive={true} />
                    </ExperimentVariant>
                  </Experiment>
                </ViewMedium>
              </Viewport>
            </section>
          </LodgingQueryParamsProvider>
        </UitkSpacing>
      </ErrorBoundary>
    );
  })
);

const Buttons = ({ withFilterButton, withMapButton }: ButtonsProps) => {
  const viewTracker = useViewTrackerContext();
  const tracker = useClickstreamAnalytics();
  const { exposure: priceSliderExperimentExposure, logExperiment: logPriceSliderExperiment } =
    useExperiment('Improve_Price_Slider_on_mweb');
  const { exposure: filterDisplayMigrationExperimentExposure, logExperiment: logFilterDisplayMigrationExperiment } = useExperiment(
    'Filter_Display_Migration_for_BEX_and_HOB_Mweb'
  );

  const logPriceSliderExposureCallback = () => {
    logPriceSliderExperiment(priceSliderExperimentExposure);
    logFilterDisplayMigrationExperiment(filterDisplayMigrationExperimentExposure);
  };

  return (
    <UitkSpacing data-stid="tools__actions">
      <UitkLayoutFlex>
        {withFilterButton && (
          <UitkLayoutFlexItem grow={2} data-stid="filters-toggle-button">
            <UitkSpacing padding={{ blockend: 'three' }}>
              <div>
                <UitkLayoutFlex>
                  <UitkLayoutFlexItem grow={2}>
                    <UniversalSortFilterWrapper
                      view={SortAndFiltersViewType.SORT_AND_FILTERS}
                      onOpenDialog={logPriceSliderExposureCallback}
                    />
                  </UitkLayoutFlexItem>
                </UitkLayoutFlex>
              </div>
            </UitkSpacing>
          </UitkLayoutFlexItem>
        )}
        {withMapButton && (
          <UitkLayoutFlexItem grow={1} data-stid="map-preview-button">
            <UitkSpacing margin={{ inline: 'one' }}>
              <div>
                <MapPreview viewTracker={viewTracker} tracker={tracker} />
              </div>
            </UitkSpacing>
          </UitkLayoutFlexItem>
        )}
      </UitkLayoutFlex>
    </UitkSpacing>
  );
};

interface ButtonsProps {
  dialogTitle: string;
  withMapButton?: boolean;
  withFilterButton?: boolean;
  classes?: string;
}

const ButtonsParent = ({ dialogTitle, withFilterButton, withMapButton }: ButtonsParentProps) => (
  <Viewport>
    <ViewSmall>
      <Buttons dialogTitle={dialogTitle} withFilterButton={true} withMapButton={true} />
    </ViewSmall>
    <ViewLarge>
      <></>
    </ViewLarge>
    <ViewExtraLarge>
      <Buttons dialogTitle={dialogTitle} withMapButton={withMapButton} withFilterButton={withFilterButton} />
    </ViewExtraLarge>
  </Viewport>
);

interface ButtonsParentProps {
  dialogTitle: string;
  withMapButton?: boolean;
  withFilterButton?: boolean;
}
